import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map, Observable, Subject, takeUntil, BehaviorSubject } from 'rxjs';
import {  UploadProgress, UploadTrackerService } from './upload-tracker.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { max } from 'lodash';
import { TranslateModule } from '@ngx-translate/core';
import { PButtonModule } from '@panaya/ui/p-button';
import { PActionButtonModule } from '@panaya/ui/p-action-button';
import { PIconModule } from '@panaya/ui/p-icon';

/**
 * Displays multiple video upload progress indicators in a floating dialog
 * Shows upload progress with step information and percentage
 * Supports minimizing, canceling individual uploads, and canceling all uploads
 */
@Component({
    selector: 'upload-progress',
    standalone: true,
    imports: [CommonModule, TranslateModule, PButtonModule, PActionButtonModule, PIconModule],
    styleUrls: ['./upload-progress.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ transform: 'translateY(100%)', opacity: 0 }),
                animate('300ms ease', style({ transform: 'translateY(0)', opacity: 1 }))
            ])
        ])
    ],
    template: `
        <!-- Floating Upload Dialog -->
        <ng-container *ngIf="uploads$ | async as uploads">
            <ng-container *ngIf="{ isMinimized: isMinimized$ | async } as state">
                <div class="upload-floating-dialog" *ngIf="uploads.length > 0" [@slideIn] (click)="maximize()">
                    <div class="dialog-container" [class.minimized]="state.isMinimized">
                        <!-- Header -->
                        <div class="dialog-header" (click)="$event.stopPropagation()"  *ngIf="!state.isMinimized">
                            <div class="header-title">
                                <span class="title-text">Uploading {{ uploads.length > 1 ? uploads.length : '' }} video evidence{{ uploads.length > 1 ? 's' : '' }}</span>
                            </div>
                            <div class="header-actions">
                                <p-icon class="minimize-button" (click)="minimize(); $event.stopPropagation()" name="minimize-2" color="functional-default" [title]="'Minimize'">
                                </p-icon>
                               <!--   <p-action-button class="cancel-all-button" (click)="cancelAll(); $event.stopPropagation()"  label="{{ 'Actions.cancel' | translate }}">
                                </p-action-button> -->
                            </div>
                        </div>

                        <div class="minimized-progress" *ngIf="state.isMinimized">
                         <div class="header-title">
                               <p-icon  class="refresh-icon" name="refresh-cw"></p-icon>
                                <span class="title-text">Uploading...</span>
                                 <span class="upload-percent">{{ getAggregatedProgress(uploads) }}%</span>
                            </div>
                            <div class="progress-bar-container">
                                <div class="minimized-progress-bar-background"></div>
                                <div class="progress-bar-fill" [style.width.%]="getAggregatedProgress(uploads)"></div>
                            </div>
                        </div>

                        <!-- Upload Progress Items -->
                        <div class="dialog-content" *ngIf="!state.isMinimized">
                        <div class="uploads-list">
                            <div
                                *ngFor="let upload of uploads; let i = index; trackBy: trackByUploadId"
                                class="upload-item"
                                [class.uploading]="upload.status === 'uploading'"
                                [class.success]="upload.status === 'success'"
                                [class.error]="upload.status === 'error'"
                            >
                                <div class="upload-content" >
                                    <!-- Icon and text container -->
                                    <div class="upload-info">
                                        <div class="upload-icon">
                                            <p-icon *ngIf="upload.status === 'uploading'" class="refresh-icon" name="refresh-cw"></p-icon>
                                            <p-icon *ngIf="upload.status === 'success'" class="checkmark" name="check-circle" color="status-progress-passed"></p-icon>
                                            <p-icon *ngIf="upload.status === 'error'" class="error-icon" name="x-circle" color="supportive-failed"></p-icon>
                                        </div>
                                        <p class="upload-text">
                                            <span class="step-label">Step {{ upload.stepNumber }}: </span>
                                            <span class="step-description">{{ upload.stepDescription }}</span>
                                        </p>
                                        <span class="upload-percent">{{ upload.percent }}%</span>
                                    </div>

                                    <!-- Progress bar -->
                                    <div class="progress-bar-container" >
                                        <div class="progress-bar-background" ></div>   <!-- ngClass="{mousehover: upload === mousehover }"-->
                                        <div class="progress-bar-fill" [style.width.%]="upload.percent"></div>
                                    </div>
                             
                                </div>

                              
                                   
                            </div>
                        </div>

                        <!-- Warning notification -->
                        <div class="upload-container-notification" [ngClass]="getNotificationClass(uploads)">
                            <div class="notification-content">
                                <p-icon class="notification-icon" name="info" *ngIf="hasActiveUploads(uploads) || hasFailedUploads(uploads)"></p-icon>
                                <p-icon class="notification-icon" name="bell" color="success" *ngIf="allUploadsSuccessful(uploads)"></p-icon>
                                <span class="notification-text" *ngIf="hasActiveUploads(uploads)">Don't close your browser while the upload is in progress.</span>
                                <span class="notification-text" *ngIf="hasFailedUploads(uploads)">Your video evidence couldn't be uploaded.</span>
                                <span class="notification-text" *ngIf="allUploadsSuccessful(uploads)">Your video evidence has been uploaded successfully!</span>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </ng-container>
        </ng-container>
    `
})
export class UploadProgressComponent implements OnInit, OnDestroy {
    uploads$: Observable<UploadProgress[]>;
    hasActiveUploads$: Observable<boolean>;
    private isMinimizedSubject = new BehaviorSubject<boolean>(false);
    isMinimized$ = this.isMinimizedSubject.asObservable();
    private destroy$ = new Subject<void>();
    private mousehover: UploadProgress | null = null;


    constructor(private uploadTracker: UploadTrackerService, private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        // Convert uploads map to sorted array observable
        this.uploads$ = this.uploadTracker.uploads$.pipe(
            takeUntil(this.destroy$),
            map((uploadsMap: Map<string, UploadProgress>) => {
                const uploads = Array.from(uploadsMap.values()).sort((a, b) => b.startTime - a.startTime);
                return uploads;
            })
        );

        // Track if there are any active uploads
        this.hasActiveUploads$ = this.uploadTracker.uploads$.pipe(
            takeUntil(this.destroy$),
            map((uploadsMap: Map<string, UploadProgress>) =>
                Array.from(uploadsMap.values()).some(upload => upload.status === 'uploading')
            )
        );
    }

    mouseHover(u: UploadProgress){
        this.mousehover = u;
        this.cd.detectChanges();

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Prevents user from closing the browser/tab while uploads are in progress
     * Shows a browser confirmation dialog if there are active uploads
     */
    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(event: BeforeUnloadEvent): void {
        // Subscribe synchronously to check current state
        let hasActiveUploads = false;
        this.uploadTracker.uploads$.pipe(
            map((uploadsMap: Map<string, UploadProgress>) =>
                Array.from(uploadsMap.values()).some(upload => upload.status === 'uploading')
            )
        ).subscribe(hasActive => hasActiveUploads = hasActive).unsubscribe();

        if (hasActiveUploads) {
            // Modern browsers require returnValue to be set
            event.preventDefault();
            event.returnValue = 'Video uploads are in progress. If you leave now, they will be cancelled. Are you sure you want to leave?';
        }
    }

    removeUpload(id: string): void {
        this.uploadTracker.removeUpload(id);
    }

    minimize(): void {
        if(!this.isMinimizedSubject.value ) {
        this.isMinimizedSubject.next(true);
        }
    }

    maximize(): void {
        if(this.isMinimizedSubject.value ) {
        this.isMinimizedSubject.next(false);
        }
    }

    cancelAll(): void {
        // TODO: Implement cancel all logic - this might need to be added to the upload tracker service
        // For now, remove all completed/failed uploads
        let uploads: UploadProgress[] = [];
        this.uploads$.pipe(takeUntil(this.destroy$)).subscribe(u => uploads = u).unsubscribe();

        uploads.forEach(upload => {
            if (upload.status !== 'uploading') {
                this.uploadTracker.removeUpload(upload.id);
            }
        });
    }

    trackByUploadId(index: number, upload: UploadProgress): string {
        return upload.id;
    }

    /**
     * Calculate aggregated progress from all non-failed uploads
     * Returns the average progress percentage
     */
    getAggregatedProgress(uploads: UploadProgress[]): number {
        // Filter out failed uploads
        const activeUploads = uploads.filter(upload => upload.status !== 'error');

        if (activeUploads.length === 0) {
            return 0;
        }

        // Calculate average progress
        const totalProgress = activeUploads.reduce((sum, upload) => sum + upload.percent, 0);
        return Math.round(totalProgress / activeUploads.length);
    }

    /**
     * Get notification CSS class based on upload status
     */
    getNotificationClass(uploads: UploadProgress[]): string {
        const hasErrors = uploads.some(upload => upload.status === 'error');
        const allSuccess = uploads.length > 0 && uploads.every(upload => upload.status === 'success');
        const hasActive = uploads.every(upload => upload.status === 'uploading');

        if (hasActive) {
            return 'warning';
        } else if (allSuccess) {
            return 'complete';
        } else if (hasErrors) {
            return 'failed';
        }
        return '';
    }

    /**
     * Check if there are any active uploads
     */
    hasActiveUploads(uploads: UploadProgress[]): boolean {
        return uploads.every(upload => upload.status === 'uploading');
    }

    /**
     * Check if all uploads are successful
     */
    allUploadsSuccessful(uploads: UploadProgress[]): boolean {
        return uploads.length > 0 && uploads.every(upload => upload.status === 'success');
    }

    /**
     * Check if there are any failed uploads
     */
    hasFailedUploads(uploads: UploadProgress[]): boolean {
        return uploads.some(upload => upload.status === 'error');
    }
}
